import React, { ElementType, ReactNode, forwardRef } from 'react';
import { LoadingSpinner, LoadingSpinnerProps } from '../LoadingSpinner';
import {
  ButtonColors,
  defaultButtonClasses,
  disabledButtonClasses,
  outlineButtonClasses,
  sizeClasses,
  transparentButtonClasses,
  underlineButtonClasses,
} from '../../styles/button';
import { merge } from '../../helpers/classes';

type AsType = { as?: ElementType };

export type ButtonProps = AsType & {
  cn?: string;
  rounded?: boolean;
  color?: ButtonColors;
  disabled?: boolean;
  enabled?: boolean;
  variant?: 'default' | 'outline' | 'underline' | 'transparent';
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  isLoading?: boolean;
  loadingSpinnerProps?: LoadingSpinnerProps;
  size?: keyof typeof sizeClasses;
  testID?: string;
  'data-testid'?: string;
} & React.ComponentPropsWithoutRef<ElementType>;

const map = {
  transparent: transparentButtonClasses,
  default: defaultButtonClasses,
  outline: outlineButtonClasses,
  underline: underlineButtonClasses,
};

const startIconClasses = 'mr-2 items-center';
const endIconClasses = 'ml-2 items-center';

// Utility function to filter props for button element
const filterProps = (props: any) => {
  const { as, testID, ...restProps } = props;
  const elementProps =
    testID && !restProps['data-testid']
      ? { ...restProps, 'data-testid': testID }
      : restProps;

  if (as === 'button') {
    const { href, target, rel, ...buttonProps } = elementProps;
    return buttonProps;
  }
  if (as === 'a') {
    const { disabled, enabled, ...anchorProps } = elementProps;
    return anchorProps;
  }
  return elementProps;
};

export const Button = forwardRef(
  (
    {
      cn = '',
      className,
      disabled,
      enabled = true,
      rounded = false,
      variant = 'default',
      color = 'default',
      startIcon,
      endIcon,
      isLoading,
      children,
      size,
      loadingSpinnerProps,
      as = 'button',
      ...rest
    }: ButtonProps,
    ref,
  ) => {
    const props = filterProps({ as, ...rest });

    const classes = map[variant](color);

    if (size) classes.push(...sizeClasses[size]);

    if (rounded) classes.push('rounded-full');

    if (disabled || !enabled) classes.push(...disabledButtonClasses(color));

    const classNames = merge(classes, cn, className);

    const Element = as;

    return (
      <Element
        {...(as === 'button' && { disabled: disabled || !enabled })}
        className={classNames}
        {...props}
        ref={ref}
      >
        {startIcon && !isLoading && (
          <div className={startIconClasses}>{startIcon}</div>
        )}
        {isLoading ? (
          <div className="relative z-0 flex">
            {startIcon && (
              <div className={merge(startIconClasses, 'opacity-0')}>
                {startIcon}
              </div>
            )}
            <div className="opacity-0">{children}</div>
            {endIcon && (
              <div className={merge(endIconClasses, 'opacity-0')}>
                {endIcon}
              </div>
            )}

            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
              <LoadingSpinner {...loadingSpinnerProps} />
            </div>
          </div>
        ) : (
          children
        )}
        {endIcon && !isLoading && (
          <div className="ml-2 items-center">{endIcon}</div>
        )}
      </Element>
    );
  },
);

Button.displayName = 'Button';
